import { useCallback, useEffect } from 'react';

import { Select, type SelectProps } from 'src/components/base';
import { withField } from 'src/helpers';
import { type IWithFieldErrorProps, withFieldError } from 'src/helpers/withFieldError';
import type { IFieldProps } from 'src/types';

type SelectFieldProps<T> = IFieldProps &
  Omit<SelectProps<T>, 'onChange'> &
  Partial<Pick<SelectProps<T>, 'onChange'>> &
  Omit<IWithFieldErrorProps, 'isShowErrorMessage'>;

export const SelectField = withField(
  withFieldError(<T,>(props: SelectFieldProps<T>) => {
    const {
      field,
      form: { setFieldValue, setFieldTouched },
      onChange,
      touchOnMount,

      ...rest
    } = props;

    const handleChange = useCallback(
      async (value: T) => {
        await setFieldValue(field.name, value);
        await setFieldTouched(field.name, true);

        onChange?.(value);
      },
      [field.name, setFieldValue, setFieldTouched, onChange]
    );

    const onBlur = useCallback(() => setFieldTouched(field.name, true), [field.name, setFieldTouched]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: false
    useEffect(() => {
      if (touchOnMount) {
        setTimeout(() => void setFieldTouched(field.name, true));
      }
    }, []);

    return <Select {...field} {...rest} onChange={handleChange} onBlur={onBlur} />;
  }),
  true
);
